import { useState, type FormEvent } from "react";
import { REAL_PLAYERS_INFO } from "../../../common/constants.ts";
import {
	CHECKPOINT_CYCLE,
	CHECKPOINT_LABELS,
	DEFAULT_BUDGETS,
	DEFAULT_FIRING,
	type MpCheckpoint,
} from "../../../common/multiplayerConfig.ts";
import type { View } from "../../../common/types.ts";
import { ActionButton } from "../../components/ActionButton.tsx";
import useTitleBar from "../../hooks/useTitleBar.tsx";
import { logEvent } from "../../util/logEvent.ts";
import { realtimeUpdate } from "../../util/realtimeUpdate.ts";
import { toWorker } from "../../util/toWorker.ts";

const showError = (error: unknown) => {
	logEvent({
		type: "error",
		text: error instanceof Error ? error.message : String(error),
		saveToDb: false,
	});
};

const displayNameFromEmail = (email: string) => email.split("@")[0] ?? "";

const AuthForm = () => {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [processing, setProcessing] = useState(false);

	const submit = async (type: "mpSignIn" | "mpSignUp") => {
		setProcessing(true);
		try {
			await toWorker("main", type, { email, password });
		} catch (error) {
			showError(error);
		}
		setProcessing(false);
	};

	const handleSubmit = async (event: FormEvent) => {
		event.preventDefault();
		await submit("mpSignIn");
	};

	return (
		<div className="row">
			<div className="col-md-6 col-lg-5">
				<div className="card">
					<div className="card-body">
						<h2 className="card-title">Sign in</h2>
						<p className="text-body-secondary">
							Multiplayer leagues are shared between you and your friends
							through a common server. Sign in with the same account on any
							computer to access your leagues.
						</p>
						<form onSubmit={handleSubmit}>
							<div className="mb-3">
								<label className="form-label" htmlFor="mp-email">
									Email address
								</label>
								<input
									id="mp-email"
									className="form-control"
									type="email"
									required
									value={email}
									onChange={(event) => {
										setEmail(event.target.value);
									}}
								/>
							</div>
							<div className="mb-3">
								<label className="form-label" htmlFor="mp-password">
									Password
								</label>
								<input
									id="mp-password"
									className="form-control"
									type="password"
									required
									minLength={6}
									value={password}
									onChange={(event) => {
										setPassword(event.target.value);
									}}
								/>
							</div>
							<div className="d-flex gap-2">
								<ActionButton type="submit" processing={processing}>
									Sign In
								</ActionButton>
								<ActionButton
									variant="secondary"
									processing={processing}
									onClick={async () => {
										await submit("mpSignUp");
									}}
								>
									Create Account
								</ActionButton>
							</div>
						</form>
					</div>
				</div>
			</div>
		</div>
	);
};

const statusBadge = (status: string) => {
	if (status === "lobby") {
		return <span className="badge text-bg-warning">Lobby</span>;
	}
	if (status === "starting") {
		return <span className="badge text-bg-info">Starting...</span>;
	}
	return <span className="badge text-bg-success">Active</span>;
};

const Multiplayer = ({ session, leagues, mpError }: View<"multiplayer">) => {
	useTitleBar({ title: "Multiplayer" });

	const [createName, setCreateName] = useState("");
	const [createDisplayName, setCreateDisplayName] = useState("");
	const [rosters, setRosters] = useState<"real" | "random">(
		REAL_PLAYERS_INFO ? "real" : "random",
	);
	const [startingSeason, setStartingSeason] = useState(
		String(REAL_PLAYERS_INFO?.MAX_SEASON ?? new Date().getFullYear()),
	);
	const [budgets, setBudgets] = useState<Record<MpCheckpoint, string>>({
		offseason: String(DEFAULT_BUDGETS.offseason),
		game25: String(DEFAULT_BUDGETS.game25),
		deadline: String(DEFAULT_BUDGETS.deadline),
		playoffs: String(DEFAULT_BUDGETS.playoffs),
		lottery: String(DEFAULT_BUDGETS.lottery),
		draft: String(DEFAULT_BUDGETS.draft),
		resign: String(DEFAULT_BUDGETS.resign),
		fa: String(DEFAULT_BUDGETS.fa),
	});
	const [faDampen, setFaDampen] = useState("0");
	const [difficulty, setDifficulty] = useState("0");
	const [autoFiring, setAutoFiring] = useState(true);
	const [noProfitYears, setNoProfitYears] = useState(
		String(DEFAULT_FIRING.noProfitYears),
	);
	const [noPlayoffsYears, setNoPlayoffsYears] = useState(
		String(DEFAULT_FIRING.noPlayoffsYears),
	);
	const [noConfFinalsYears, setNoConfFinalsYears] = useState(
		String(DEFAULT_FIRING.noConfFinalsYears),
	);
	const [failUpwardPct, setFailUpwardPct] = useState(
		String(DEFAULT_FIRING.failUpwardPct),
	);
	const [inviteCode, setInviteCode] = useState("");
	const [joinDisplayName, setJoinDisplayName] = useState("");
	const [processing, setProcessing] = useState(false);

	if (mpError) {
		return (
			<div className="alert alert-danger d-inline-block">
				Could not reach the multiplayer server: {mpError}
			</div>
		);
	}

	if (!session) {
		return <AuthForm />;
	}

	const defaultDisplayName = displayNameFromEmail(session.email);

	const handleCreate = async (event: FormEvent) => {
		event.preventDefault();
		setProcessing(true);
		try {
			const budgetSettings = Object.fromEntries(
				CHECKPOINT_CYCLE.map((checkpoint) => [
					checkpoint,
					Number.parseInt(budgets[checkpoint]) >= 0
						? Number.parseInt(budgets[checkpoint])
						: DEFAULT_BUDGETS[checkpoint],
				]),
			);
			const result = await toWorker("main", "mpCreateLobby", {
				name: createName,
				displayName: createDisplayName || defaultDisplayName,
				settings: {
					...(rosters === "real"
						? { rosters, startingSeason: Number.parseInt(startingSeason) }
						: { rosters }),
					budgets: budgetSettings,
					difficulty: Number.parseFloat(difficulty) || 0,
					faWinnerDampen: Number.parseFloat(faDampen) || 0,
					autoFiring,
					noProfitYears: Number.parseInt(noProfitYears) || undefined,
					noPlayoffsYears: Number.parseInt(noPlayoffsYears) || undefined,
					noConfFinalsYears: Number.parseInt(noConfFinalsYears) || undefined,
					failUpwardPct: Number.isFinite(Number.parseFloat(failUpwardPct))
						? Number.parseFloat(failUpwardPct)
						: undefined,
				},
			});
			await realtimeUpdate([], `/multiplayer/lobby/${result.league_id}`);
		} catch (error) {
			showError(error);
		}
		setProcessing(false);
	};

	const handleJoin = async (event: FormEvent) => {
		event.preventDefault();
		setProcessing(true);
		try {
			const result = await toWorker("main", "mpJoinLobby", {
				inviteCode,
				displayName: joinDisplayName || defaultDisplayName,
			});
			if (result.status === "joined") {
				await realtimeUpdate([], `/multiplayer/lobby/${result.league_id}`);
			} else if (result.status === "not_found") {
				showError(new Error("No league found with that invite code."));
			} else if (result.status === "version_mismatch") {
				showError(
					new Error(
						"Your game version does not match this league. Everyone in a league must run the same version - git pull and reinstall, or ask the commissioner to.",
					),
				);
			} else if (result.status === "already_started") {
				showError(
					new Error(
						"That league has already started, so new members cannot join.",
					),
				);
			}
		} catch (error) {
			showError(error);
		}
		setProcessing(false);
	};

	return (
		<>
			<div className="d-flex align-items-center mb-3 gap-2">
				<span className="text-body-secondary">
					Signed in as {session.email}
				</span>
				<button
					className="btn btn-sm btn-light-bordered"
					onClick={async () => {
						try {
							await toWorker("main", "mpSignOut", undefined);
						} catch (error) {
							showError(error);
						}
					}}
				>
					Sign Out
				</button>
			</div>

			<div className="row gy-3 mb-4">
				<div className="col-md-6 col-lg-5">
					<div className="card h-100">
						<div className="card-body">
							<h2 className="card-title">Create a lobby</h2>
							<p className="text-body-secondary">
								Start a new multiplayer league and invite your friends with the
								code it generates.
							</p>
							<form onSubmit={handleCreate}>
								<div className="mb-3">
									<label className="form-label" htmlFor="mp-create-name">
										League name
									</label>
									<input
										id="mp-create-name"
										className="form-control"
										type="text"
										required
										value={createName}
										onChange={(event) => {
											setCreateName(event.target.value);
										}}
									/>
								</div>
								<div className="mb-3">
									<label className="form-label" htmlFor="mp-create-display">
										Your name
									</label>
									<input
										id="mp-create-display"
										className="form-control"
										type="text"
										placeholder={defaultDisplayName}
										value={createDisplayName}
										onChange={(event) => {
											setCreateDisplayName(event.target.value);
										}}
									/>
								</div>
								{REAL_PLAYERS_INFO ? (
									<div className="mb-3">
										<label className="form-label" htmlFor="mp-create-rosters">
											Players
										</label>
										<select
											id="mp-create-rosters"
											className="form-select"
											value={rosters}
											onChange={(event) => {
												setRosters(event.target.value as "real" | "random");
											}}
										>
											<option value="real">Real players</option>
											<option value="random">Random players</option>
										</select>
									</div>
								) : null}
								{REAL_PLAYERS_INFO && rosters === "real" ? (
									<div className="mb-3">
										<label className="form-label" htmlFor="mp-create-season">
											Starting season
										</label>
										<input
											id="mp-create-season"
											className="form-control"
											type="number"
											min={REAL_PLAYERS_INFO.MIN_SEASON}
											max={REAL_PLAYERS_INFO.MAX_SEASON}
											required
											value={startingSeason}
											onChange={(event) => {
												setStartingSeason(event.target.value);
											}}
										/>
									</div>
								) : null}
								<div className="mb-3">
									<label className="form-label" htmlFor="mp-create-difficulty">
										Difficulty
									</label>
									<select
										id="mp-create-difficulty"
										className="form-select"
										value={difficulty}
										onChange={(event) => {
											setDifficulty(event.target.value);
										}}
									>
										<option value="-0.25">Easy</option>
										<option value="0">Normal</option>
										<option value="0.25">Hard</option>
										<option value="1">Insane</option>
									</select>
								</div>
								<div className="mb-3">
									<label
										className="form-label"
										htmlFor="mp-create-fa-dampen"
										title="0 = players favor winning teams like normal BBGM; higher values level the free agency playing field so champions can't hoard stars"
									>
										FA winner dampening (0-1)
									</label>
									<input
										id="mp-create-fa-dampen"
										className="form-control"
										type="number"
										min={0}
										max={1}
										step={0.1}
										value={faDampen}
										onChange={(event) => {
											setFaDampen(event.target.value);
										}}
									/>
								</div>
								<div className="mb-3">
									<label className="form-check-label d-block mb-1">
										<input
											className="form-check-input me-1"
											type="checkbox"
											checked={autoFiring}
											onChange={(event) => {
												setAutoFiring(event.target.checked);
											}}
										/>
										Auto-fire GMs (commissioner rules)
									</label>
									{autoFiring ? (
										<div className="row g-2">
											<div className="col-6 col-md-3">
												<label
													className="form-label small text-body-secondary mb-0"
													htmlFor="mp-create-noprofit"
												>
													Seasons w/o profit
												</label>
												<input
													id="mp-create-noprofit"
													className="form-control form-control-sm"
													type="number"
													min={1}
													max={30}
													value={noProfitYears}
													onChange={(event) => {
														setNoProfitYears(event.target.value);
													}}
												/>
											</div>
											<div className="col-6 col-md-3">
												<label
													className="form-label small text-body-secondary mb-0"
													htmlFor="mp-create-noplayoffs"
												>
													Seasons missing playoffs
												</label>
												<input
													id="mp-create-noplayoffs"
													className="form-control form-control-sm"
													type="number"
													min={1}
													max={30}
													value={noPlayoffsYears}
													onChange={(event) => {
														setNoPlayoffsYears(event.target.value);
													}}
												/>
											</div>
											<div className="col-6 col-md-3">
												<label
													className="form-label small text-body-secondary mb-0"
													htmlFor="mp-create-noconf"
												>
													Seasons w/o conf. finals
												</label>
												<input
													id="mp-create-noconf"
													className="form-control form-control-sm"
													type="number"
													min={1}
													max={30}
													value={noConfFinalsYears}
													onChange={(event) => {
														setNoConfFinalsYears(event.target.value);
													}}
												/>
											</div>
											<div className="col-6 col-md-3">
												<label
													className="form-label small text-body-secondary mb-0"
													htmlFor="mp-create-failupward"
													title="Chance a fired GM lands on a top-4 team instead of the worst available"
												>
													Fail-upward (0-1)
												</label>
												<input
													id="mp-create-failupward"
													className="form-control form-control-sm"
													type="number"
													min={0}
													max={1}
													step={0.01}
													value={failUpwardPct}
													onChange={(event) => {
														setFailUpwardPct(event.target.value);
													}}
												/>
											</div>
										</div>
									) : null}
								</div>
								<div className="mb-3">
									<label className="form-label">
										Trades allowed per checkpoint
									</label>
									<div className="row g-2">
										{CHECKPOINT_CYCLE.filter(
											// Playoffs never allows trading (rosters locked for the
											// bracket); every other checkpoint's budget is adjustable
											(checkpoint) => checkpoint !== "playoffs",
										).map((checkpoint) => (
											<div className="col" key={checkpoint}>
												<label
													className="form-label small text-body-secondary mb-0"
													htmlFor={`mp-budget-${checkpoint}`}
												>
													{CHECKPOINT_LABELS[checkpoint]}
												</label>
												<input
													id={`mp-budget-${checkpoint}`}
													className="form-control"
													type="number"
													min={0}
													max={99}
													value={budgets[checkpoint]}
													onChange={(event) => {
														setBudgets((prev) => ({
															...prev,
															[checkpoint]: event.target.value,
														}));
													}}
												/>
											</div>
										))}
									</div>
								</div>
								<ActionButton type="submit" processing={processing}>
									Create Lobby
								</ActionButton>
							</form>
						</div>
					</div>
				</div>

				<div className="col-md-6 col-lg-5">
					<div className="card h-100">
						<div className="card-body">
							<h2 className="card-title">Join a lobby</h2>
							<p className="text-body-secondary">
								Enter the invite code you got from your league's commissioner.
							</p>
							<form onSubmit={handleJoin}>
								<div className="mb-3">
									<label className="form-label" htmlFor="mp-join-code">
										Invite code
									</label>
									<input
										id="mp-join-code"
										className="form-control text-uppercase"
										type="text"
										required
										maxLength={6}
										value={inviteCode}
										onChange={(event) => {
											setInviteCode(event.target.value.toUpperCase());
										}}
									/>
								</div>
								<div className="mb-3">
									<label className="form-label" htmlFor="mp-join-display">
										Your name
									</label>
									<input
										id="mp-join-display"
										className="form-control"
										type="text"
										placeholder={defaultDisplayName}
										value={joinDisplayName}
										onChange={(event) => {
											setJoinDisplayName(event.target.value);
										}}
									/>
								</div>
								<ActionButton type="submit" processing={processing}>
									Join Lobby
								</ActionButton>
							</form>
						</div>
					</div>
				</div>
			</div>

			<h2>My multiplayer leagues</h2>
			{leagues.length === 0 ? (
				<p className="text-body-secondary">
					None yet. Create a lobby or join one with an invite code.
				</p>
			) : (
				<div className="table-responsive">
					<table className="table table-striped table-hover align-middle w-auto">
						<thead>
							<tr>
								<th>Name</th>
								<th>Status</th>
								<th>Invite code</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{leagues.map((league) => (
								<tr key={league.id}>
									<td>{league.name}</td>
									<td>{statusBadge(league.status)}</td>
									<td>
										<code>{league.invite_code}</code>
									</td>
									<td>
										<a
											className="btn btn-sm btn-light-bordered"
											href={`/multiplayer/lobby/${league.id}`}
										>
											Open
										</a>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</>
	);
};

export default Multiplayer;
